/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Col,
  Modal,
  notification,
  Row,
  Space,
  TablePaginationConfig,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined, DeleteFilled } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { IProduct, ISearchProductReq } from '@/interfaces/product';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { importProduct } from '@/adapters/product';
import { twMerge } from 'tailwind-merge';
import { formatNumber } from '@/utils/dataUtils';
import { isEmpty, isUndefined } from 'lodash';

interface IProps {
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const _criteria: ISearchProductReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const ImportProductModal = ({ open, handleOk, handleCancel }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchProductReq>({
    ..._criteria,
  });
  const [product, setProduct] = useState<IProduct[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IProduct> | SorterResult<IProduct>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IProduct) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const columns: TableProps<IProduct>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      className: 'font-bold',
    },
    {
      title: 'หน่วย Package',
      dataIndex: 'packageUnitId',
      key: 'packageUnitId',
      className: 'font-bold',
    },
    {
      title: 'Conversion',
      dataIndex: 'conversion',
      key: 'conversion',
      align: 'center',
      className: 'font-bold',
    },
    {
      title: 'หน่วยขาย',
      dataIndex: 'baseUnitId',
      key: 'baseUnitId',
      className: 'font-bold',
    },
    {
      title: 'ราคาขาย/หน่วย',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      className: 'font-bold text-blue-700',
      render: _ => formatNumber(_),
    },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      align: 'center',
      width: 80,
      className: 'font-bold',
      render: _ => (
        <Space
          className={twMerge(
            _ === 2 && 'text-red-600',
            _ === 3 && 'text-green-700',
          )}
        >
          {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => setProduct(c => c.filter(x => x.code !== rec.code))}
        />
      ),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setProduct([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelData(file)) as any[];
      // excelData = excelData.map(c => {
      //   c.status = 1;
      //   c.remark = c.remark?.toString() || '';
      //   return c;
      // });

      excelData = excelData
        .slice(1)
        .filter(c => !isEmpty(c[0]) || !isUndefined(c[0]))
        .map((c: any) => {
          const product: IProduct = {
            code: c[0]?.toString(),
            name: c[1]?.toString(),
            packageUnitId: c[2]?.toString(),
            conversion: Number(c[3] || 0),
            baseUnitId: c[4]?.toString(),
            unitPrice: Number(c[5] || 0),
            remark: c[6]?.toString(),
            status: 1,
          } as IProduct;
          return product;
        });
      setProduct(excelData);
      return false;
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const mutations = useMutation({
    mutationFn: async () => await importProduct(product),
    mutationKey: ['import-product'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        setProduct([]);
        notification.success({
          message: 'นำเข้าสินค้าสำเร็จ',
        });
        handleOk();
      } else {
        const duplicateProducts: string[] = data.data?.data;
        const updateProduct = product.map(c => {
          c.status = duplicateProducts.includes(c.code) ? 2 : 3;
          return c;
        });
        setProduct(updateProduct);
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    mutations.mutateAsync();
  };

  return (
    <Modal
      title="นำเข้าสินค้า"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-2 w-full">
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Upload {...props}>
              {fileList.length === 0 && (
                <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
              )}
            </Upload>
          </Col>
        </Row>
        <TableComponent<IProduct>
          columns={columns}
          criteria={criteria}
          data={{
            data: product,
            totalRecords: product.length,
          }}
          loading={false}
          onTableChange={onTableChange}
          // rowSelection={isMultiSelect ? rowSelection : undefined}
          scroll
        />
      </div>
    </Modal>
  );
};

export default ImportProductModal;
